<template>
  <ErrorBoundary>
    <div class="tw-flex">
      <!-- Top of Sidebar -->
      <ShrinksWithNavBar>
        <div class="tw-flex tw-items-center tw-p-2">
          <router-link
            :to="{
              name: 'Dashboard',
            }">
            <img
              class="tw-object-contain tw-max-h-16 tw-p-3 tw-max-w-xs tw-h-16 tw-w-full"
              :src="(globalStore.wlPreviewData.productLogo as string) || `/api/get-logo.svg?type=brand&brand=${globalStore.brand}`"
              alt="platform logo" />
          </router-link>
          <NavigationCollapseButton
            class="tw-ml-auto"
            @click="uiStore.sideBarCollapsed = !uiStore.sideBarCollapsed"
            data-test="navigation-collapse-button" />
        </div>
      </ShrinksWithNavBar>
      <div
        class="tw-my-auto tw-ml-4"
        v-if="uiStore.sideBarCollapsed">
        <NavigationCollapseButton
          class="tw-transition-opacity tw-duration-300"
          :class="uiStore.sideBarCollapsed ? 'tw-opacity-100' : 'tw-opacity-0'"
          @click="uiStore.sideBarCollapsed = !uiStore.sideBarCollapsed"
          :disabled="!uiStore.sideBarCollapsed"
          data-test="navigation-open-button" />
      </div>

      <!-- Actual Header -->
      <div class="tw-px-4 tw-py-2 tw-my-auto tw-w-full tw-flex tw-items-center">
        <!-- Breadcrumbs -->
        <div class="tw-hidden md:tw-block">
          <BreadCrumbs />
        </div>
        <!-- Icons -->
        <div class="tw-flex tw-items-center tw-ml-auto">
          <GuidedToursMenu />

          <GuidedTourTarget targetID="add-device-modal-open">
            <RoundDoubleIconButton
              v-if="canAddDevice"
              icon-inner="hard-drive"
              icon-outer="plus"
              :outlined="false"
              data-test="open-add-device-modal"
              @click="handleAddDeviceButton" />
          </GuidedTourTarget>
          <RoundIconButton
            :outlined="false"
            fa-icon="circle-question"
            @click="handleModalOpen('support')" />
          <div class="tw-h-8 tw-w-[1px] tw-bg-primary-50 tw-mx-2" />
          <LanguageSelector
            show-generic-opener
            v-if="!isReactNative" />
          <div class="list-icon_item dropdown">
            <a
              data-test="header-profile-dropdown-toggle"
              href="#"
              class="btnModal dropdown-toggle"
              role="button"
              data-bs-toggle="dropdown">
              <FontAwesomeIcon
                icon="user"
                class="tw-m-auto tw-text-xl tw-mt-2 tw-text-primary-300 hover:tw-text-primary-400" />
              <div
                v-if="newsList.filter((e) => !e.hasRead).length > 0"
                id="profileNewsIndicator"
                class="tw-bg-primary-500" />
            </a>
            <div
              class="dropdown-menu py-0 rounded"
              style="width: 150px">
              <div class="tw-bg-primary-500 tw-text-primary-contrast tw-px-2 tw-py-4 tw-rounded-t">
                <div class="tw-font-bold tw-text-center tw-break-words">
                  {{ userName }}
                </div>
              </div>

              <!-- Profile -->
              <RouterLink
                to="/profile"
                class="tw-flex tw-items-center tw-py-2">
                <div class="tw-w-8 tw-flex tw-justify-center">
                  <font-awesome-icon
                    class="tw-text-primary-600"
                    icon="fa-solid fa-user" />
                </div>
                <div
                  class="text-body tw-px-1"
                  v-html="t('profileDropdown.profile')"></div>
              </RouterLink>
              <!-- What's new -->
              <RouterLink
                class="tw-flex tw-items-center tw-py-2"
                to="/whats-new"
                data-test="profile_dropdown_whats">
                <div class="tw-w-8 tw-flex tw-justify-center">
                  <font-awesome-icon
                    class="tw-text-primary-600"
                    icon="fa-bullhorn" />
                </div>
                <div
                  class="text-body tw-px-1"
                  v-html="t('profileDropdown.whatsnew')"></div>
                <div
                  v-if="newsList.filter((e) => !e.hasRead).length > 0"
                  class="tw-rounded-full tw-bg-primary-500 tw-text-white tw-px-2 tw-py-0.5 tw-text-xs tw-ml-1">
                  {{ newsList.filter((e) => !e.hasRead).length }}
                </div>
              </RouterLink>

              <!-- User Management -->
              <RouterLink
                v-if="userStore.canManageUsers"
                class="tw-flex tw-items-center tw-py-2"
                :to="{
                  name: 'Users Overview',
                }">
                <div class="tw-w-8 tw-flex tw-justify-center">
                  <font-awesome-icon
                    class="tw-text-primary-600"
                    icon="fa-users-gear" />
                </div>
                <div
                  class="text-body tw-px-1"
                  v-html="t('user.user_management')"></div>
              </RouterLink>

              <!-- Remote Control -->
              <RouterLink
                v-if="userStore.canManageRemoteControl"
                class="tw-flex tw-items-start tw-py-2"
                :to="{
                  name: 'Remote Control Overview',
                }">
                <div class="tw-w-8 tw-flex tw-justify-center">
                  <font-awesome-icon
                    class="tw-text-primary-600 tw-mt-0.5"
                    icon="fa-screwdriver-wrench" />
                </div>
                <div
                  class="text-body tw-px-1 tw-flex-1 leading-snug"
                  v-html="t('remote-control.manage_remote_control')"></div>
              </RouterLink>

              <!-- WL Preview -->
              <RouterLink
                v-if="userStore.hasWLTools"
                class="tw-flex tw-items-center tw-py-2"
                :to="{
                  name: 'White Label Management',
                }">
                <div class="tw-w-8 tw-flex tw-justify-center">
                  <font-awesome-icon
                    class="tw-text-primary-600"
                    icon="fa-gear" />
                </div>
                <div class="text-body tw-px-1">Manage WL</div>
              </RouterLink>

              <!-- Logout -->
              <RouterLink
                class="tw-flex tw-items-center tw-py-2"
                to="/logout"
                data-test="header-profile-dropdown-logout">
                <div class="tw-w-8 tw-flex tw-justify-center">
                  <font-awesome-icon
                    class="tw-text-primary-600"
                    icon="fa-solid fa-right-from-bracket" />
                </div>
                <div
                  class="text-body tw-px-1"
                  v-html="t('profileDropdown.logout')"></div>
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- PM Account Warning -->
    <div
      v-if="message"
      data-test="header-pm-banner">
      <div class="tw-bg-secondary-500 tw-h-1" />
      <div class="flex tw-relative">
        <div
          class="tw-absolute tw-z-[41] tw-text-xs tw-text-primary-contrast tw-font-semibold tw-bg-secondary-500 tw-inline-block tw-pb-1 tw-px-2 tw-ml-8 tw-rounded-b-lg">
          {{ message }}
        </div>
      </div>
    </div>

    <FullPageModal
      :isOpen="openModal"
      :onCloseModal="closeModal"
      :headerText="modalHeaderTextHandler()">
      <SupportForm
        v-if="modalSubject === 'support'"
        @reset="closeModal" />
    </FullPageModal>
  </ErrorBoundary>
</template>

<script setup lang="ts">
import GuidedTourTarget from "@/components/library/GuidedTour/GuidedTourTarget.vue";
import RoundIconButton from "@/components/library/RoundIconButton/RoundIconButton.vue";
import { DXNews } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import * as Sentry from "@sentry/vue";
import { computed, ComputedRef, onMounted, ref } from "vue";
import SupportForm from "../elements/SupportForm/SupportForm.vue";
import ErrorBoundary from "../library/ErrorBoundary/ErrorBoundary.vue";
import FullPageModal from "../library/FullPageModal/FullPageModal.vue";
import RoundDoubleIconButton from "../library/RoundDoubleIconButton/RoundDoubleIconButton.vue";
import BreadCrumbs from "./BreadCrumbs/BreadCrumbs.vue";
import NavigationCollapseButton from "./navigation/NavigationCollapsButton.vue";
import ShrinksWithNavBar from "./navigation/ShrinksWithNavBar.vue";
import { useTranslation } from "i18next-vue";
import { useAppWrapper } from "@/composables/useAppWrapper";
import LanguageSelector from "./LanguageSelector/LanguageSelector.vue";
import { useGlobalConfigsStore } from "@/storeModules/globalConfigs/globalConfigs";
import { useUserConfigStore } from "@/storeModules/userConfigs";
import { useUISettingsStore } from "@/storeModules/uisettings/UISettingsStore";
import GuidedToursMenu from "./GuidedToursMenu/GuidedToursMenu.vue";

const { t } = useTranslation();
const { isReactNative } = useAppWrapper();
const globalStore = useGlobalConfigsStore();
const userStore = useUserConfigStore();
const uiStore = useUISettingsStore();
const { canAddDevice } = useUserConfigStore();

const message = computed(() => globalStore.bannerMessage);

const handleAddDeviceButton = () => {
  uiStore.addDeviceModalOpen = true;
  Sentry.setTag("a-b", "add device");
  Sentry.captureMessage("add device opened through header", "info");
};

const openModal = ref(false);
const modalSubject = ref("");

const handleModalOpen = (modal: "support" | "addDevice") => {
  modalSubject.value = modal;
  openModal.value = true;
};

const closeModal = () => {
  openModal.value = false;
};

const modalHeaderTextHandler = () => {
  switch (modalSubject.value) {
    case "addDevice":
      return t("modals.modal_device_add_title");
    case "support":
      return t("modals.support_title");
    default:
      return "";
  }
};

const userName: ComputedRef<string> = computed(() => {
  return userStore.userData.user ?? "";
});

const newsList: ComputedRef<DXNews[]> = computed(() => {
  return userStore.newsList;
});

onMounted(async () => {
  await globalStore.fetchBannerMessage();
});
</script>

<style scoped lang="postcss">
#profileNewsIndicator {
  @apply tw-bg-primary-500;
  width: 8px;
  height: 8px;
  position: absolute;
  top: 2px;
  right: 6px;
  border-radius: 10px;
}
</style>